import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTabletOrMobile } from "../../../hooks/responsive";

// Leader data with real-looking photos
const leaderData = [
  {
    key: "Cindy",
    thumbnailSrc: "/images/leader-sarah.jpg",
  },

  {
    key: "Jordan",
    thumbnailSrc: "/images/leader-yuki.jpg",
  },
  {
    key: "Cheryl",
    thumbnailSrc: "/images/leader-alex.jpg",
  },
  {
    key: "Evan",
    thumbnailSrc: "/images/leader-ming.jpg",
  },
  {
    key: "Olivia",
    thumbnailSrc: "/images/leader-marcus.jpg",
  },
  {
    key: "Nic",
    thumbnailSrc: "/images/leader-priya.jpg",
  },
];

const LeaderShowcase = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [desktopPage, setDesktopPage] = useState(0);
  const isMobile = useTabletOrMobile();

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    if (newDirection < 0) {
      if (currentIndex === 0) {
        setCurrentIndex(leaderData.length - 1);
      } else {
        setCurrentIndex(currentIndex - 1);
      }
    } else {
      if (currentIndex === leaderData.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  const nextDesktopPage = () => {
    setDesktopPage((prev) => {
      if ((prev + 1) * 3 >= leaderData.length) {
        return 0;
      }
      return prev + 1;
    });
  };

  const variants = {
    enter: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      rotate: direction < 0 ? 10 : -10,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      rotate: 0,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      rotate: direction > 0 ? 10 : -10,
    }),
  };

  const LeaderCard = ({ leader }) => {
    const name = t(`leader_programme.showcase.leaders.${leader.key}.name`);
    const quote = t(`leader_programme.showcase.leaders.${leader.key}.quote`);

    return (
      <div className="tw-relative tw-overflow-hidden tw-rounded-3xl">
        <div className="tw-relative tw-w-full">
          <img
            className="tw-pointer-events-none tw-h-[60vh] tw-w-full tw-object-cover md:tw-h-[400px]"
            src={leader.thumbnailSrc}
            alt={name}
          />
          <div
            className="tw-absolute tw-inset-0"
            style={{
              background:
                "linear-gradient(180.04deg, rgba(0, 39, 51, 0.50) 0%, rgba(0, 39, 51, 0) 30%, rgba(0, 39, 51, 0) 60%, rgba(0, 39, 51, 0.50) 85%)",
            }}
          />
        </div>

        {/* Name at top */}
        <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-flex tw-flex-col tw-rounded-3xl tw-px-4 tw-pt-4">
          <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-0.5 tw-text-center tw-text-[10px] tw-font-bold tw-text-white">
            {t("leader_programme.showcase.meet_label")}
            <span className="tw-text-2xl tw-font-bold">{name}</span>
          </div>
        </div>

        {/* Quote at bottom */}
        <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4 tw-flex tw-items-end tw-gap-2 tw-rounded-3xl tw-pt-4">
          <div className="tw-grow">
            <div className="tw-mb-1 tw-text-base tw-italic tw-text-white">"{quote}"</div>
            <div className="tw-text-xs tw-font-semibold tw-text-white/70">
              {t(`leader_programme.showcase.leaders.${leader.key}.trips_led`)}
            </div>
          </div>
          {isMobile && (
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => paginate(-1)}
              className="tw-rounded-full tw-bg-white/80 tw-p-3 tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-white/90"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="tw-h-6 tw-w-6 tw-text-primary-main"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </motion.button>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="tw-flex tw-snap-start tw-flex-col tw-justify-center tw-overflow-hidden tw-bg-[#EBFAFF] tw-px-4 tw-pb-[40px] tw-pt-[40px] md:tw-px-16 md:tw-pb-20 md:tw-pt-16 lg:tw-px-24">
      <div className="tw-mb-1 tw-text-center tw-text-[26px] tw-font-extrabold md:tw-text-[42px]">
        {t("leader_programme.showcase.title")}
      </div>
      <div className="tw-mx-auto tw-mb-8 tw-max-w-2xl tw-text-center tw-text-base tw-font-semibold tw-text-primary-main md:tw-mb-12 md:tw-text-xl">
        {t("leader_programme.showcase.subtitle")}
      </div>

      <div className="tw-mx-auto tw-w-full tw-max-w-[1200px]">
        {/* Mobile View - Swipeable cards */}
        <div className="tw-relative tw-mb-8 tw-h-[60vh] md:tw-hidden">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
                rotate: { duration: 0.5 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="tw-absolute tw-w-full tw-cursor-grab"
            >
              <LeaderCard leader={leaderData[currentIndex]} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop View - 3 column grid with pagination */}
        <div className="tw-hidden md:tw-block">
          <div className="tw-relative tw-grid tw-gap-6 md:tw-grid-cols-3">
            <AnimatePresence initial={false} mode="wait">
              {leaderData.slice(desktopPage * 3, desktopPage * 3 + 3).map((leader, index) => (
                <motion.div
                  key={`${desktopPage}-${index}`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -50 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.15,
                    ease: "easeOut",
                  }}
                  className="tw-will-change-transform"
                >
                  <LeaderCard leader={leader} />
                </motion.div>
              ))}
            </AnimatePresence>
            {/* Next page button */}
            <div className="tw-absolute tw-right-[-60px] tw-top-1/2 tw--translate-y-1/2 tw-transform">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={nextDesktopPage}
                className="tw-rounded-full tw-bg-white/80 tw-p-3 tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-white/90"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="tw-h-6 tw-w-6 tw-text-primary-main"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Page indicators for mobile */}
      <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-2 md:tw-hidden">
        {leaderData.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`tw-h-2 tw-w-2 tw-rounded-full tw-transition-colors ${
              index === currentIndex ? "tw-bg-primary-main" : "tw-bg-gray-300"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default LeaderShowcase;
